import type { Accessor } from 'solid-js'
import type { SetStoreFunction } from 'solid-js/store'
import { ObjectImpl, type IObjectImpl, type SetPosFn, type HugFit, type GeometrySeed, type SelectionBox, ObjectType, ResizeHandles } from '../interactions/ObjectImpl'
import type { TextResizeMode } from '../interactions/ResizeMode'
import type { SelectionContext } from '../selection'
import type { Mode } from '../../project/PosterMode'
import { afterRender } from '../utils'
import { geometryReady, beginFit, endFit, wrapText, onGeometryReady, MIN_TEXT_BOX } from './fitText'
import type { ObjectContext } from '../../project/objects'
import type { ImplRegistry } from '../interactions/ObjectImpl'
import type { HistoryContext } from '../../project/history'
import type { RuntimeText, Box, Dimensions } from '../../types'
import { cloneBox } from 'Shared/posters/types'
import type { Style } from '../../project/posterSchema'

export type { SetPosFn } from '../interactions/ObjectImpl'

/* See TEXT_OBJECT_DESIGN.txt for some requirements. */

export interface ITextImpl extends IObjectImpl
{
	resizeHandles(): ResizeHandles;   // Actually only  should be 'textCorner' | 'fieldBox';

	verticalAlign(): 'top' | 'center' | 'bottom'

	displayText(): string

	yOffset(): number
	overrideFontSize(): number | null
	getWrappedLines(width: Accessor<number>): (() => string[]) | undefined

	boxWidth():    number | undefined;

	countTextLines(): number

	minLines():  number
	minHeight(): number

	getText():          string
	getLineHeight():    number
	getTextAlign():     string
	hasMultilineText(): boolean

	setContentOffsetY(v: number): void

	// ── Host-driven edit / resize surface ────────────────────────────────────────
	// Called by SelectableObject (double-click edit, mouseup commit) and ResizeHandles (the corner /
	// text-width / field-width drags). Declared here so those hosts reach them through ITextImpl after
	// narrowing, rather than the former `as unknown` casts and optional-chained no-ops. Every concrete
	// text impl already implements them (see AbstractTextImpl's abstract members).
	initialText(): string
	onTextEdit(text: string, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext): void

	getWrappedHeight(w: number): number | undefined
	startFontSize(): number | undefined
	onCornerResize(fontSize: number, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext): void
	onCornerResizeEnd(box: Box, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext): void
	onWidthResize(w: number): void
	onWidthResizeEnd(box: Box, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext): void
	onFieldWidthResize(w: number): void
	onFieldWidthResizeEnd(left: number, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext): void
}

export abstract class AbstractTextImpl extends ObjectImpl
{
	type() { return 'text' as ObjectType; }

	readonly id: string;

	abstract displayText(): string;

	abstract boxWidth():    number | undefined
	abstract getText():     string
	abstract initialText(): string
	abstract onTextEdit(text: string, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext): void

	abstract yOffset(): number
	abstract overrideFontSize(): number | null
	abstract getWrappedLines(width: Accessor<number>): (() => string[]) | undefined

	abstract fitAfterEdit(snap: Box, setPos: SetPosFn, coy: number, setCoy: (v: number) => void, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext, canvas: Dimensions): boolean
	abstract getWrappedHeight(w: number): number | undefined
	abstract onCornerResize(fontSize: number, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext): void
	abstract onCornerResizeEnd(box: Box, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext): void
	abstract startFontSize(): number | undefined
	abstract onWidthResize(w: number): void
	abstract onWidthResizeEnd(box: Box, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext): void
	abstract onFieldWidthResize(w: number): void
	abstract onFieldWidthResizeEnd(left: number, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext): void

	abstract getHugFit(): HugFit | undefined

	constructor(
		protected object:     RuntimeText,
		protected userFacing: Accessor<boolean>,
		protected canvas:     Dimensions,
		protected effStyle:   Accessor<Style | undefined>,
		protected lineHeight: Accessor<number>,
		protected textAlign:  Accessor<string>,
		protected textRef:    (() => SVGTextElement | null | undefined) | undefined,
		protected getContentRef: () => SVGGElement | undefined,
		box:    Box,
		setBox: SetStoreFunction<Box>,
		seed:   GeometrySeed,
		public contentOffsetY:    Accessor<number>,
		public setContentOffsetY: (v: number) => void
	)
	{
		super(box,setBox,seed);
		this.id  = object.id;
	}

	override writeGeometry(box: Box): void
	{
		if (!geometryReady()) return
		super.writeGeometry(box)
	}

	getTextEl(): SVGTextElement | null { return this.textRef?.() ?? null }

	getLineHeight():  number  { return this.lineHeight() }
	getTextAlign():   string  { return this.textAlign() }
	hasMultilineText(): boolean { return this.countTextLines() > 1 }

	isField(): boolean { return !!this.object.style?.isField; }

	verticalAlign(): 'top' | 'center' | 'bottom' { return this.object.alignment ?? 'top' }

	countTextLines(): number
	{
		const wrapped = this.getWrappedLines(() => this.boxWidth() ?? 0)
		return (wrapped ? wrapped() : this.displayText().split('\n')).length
	}

	minLines(): number { return (this.object.content ?? '').split('\n').length }

	minHeight(): number
	{
		const font = (this.effStyle()?.fontSize ?? 24) as number
		return Math.ceil(this.minLines() * font * this.getLineHeight())
	}

	// The font the wrap/measure math runs at: a concrete impl's overrideFontSize() when it has one
	// (PlainWrap's frozen/design font), else the style font. PlainTextImpl's overrideFontSize() is
	// null, so it resolves to the style font — matching its former private copy.
	protected wrapFontSize(): number
	{
		return (this.overrideFontSize() ?? this.effStyle()?.fontSize ?? 16) as number
	}

	// Height of the text wrapped to `newWidth` at wrapFontSize() — shared by the plain and user text
	// impls (identical bodies).
	protected userWrappedHeight(newWidth: number): number
	{
		const fs    = this.wrapFontSize()
		const lines = wrapText(this.displayText(), newWidth, { ...this.effStyle(), fontSize: fs })
		return Math.max(MIN_TEXT_BOX, Math.ceil(lines.length * fs * this.lineHeight()))
	}

	// Measure the rendered content group's ink box as a hug fit. Returns measured:false (with
	// `fallbackWidth ?? 30` for the width) until the group has laid out — the fit loop retries on that.
	// That unmeasured default is also what an *empty* text object settles on (no ink to hug), and is the
	// only place a text box gets a minimum big enough to grab; a measured box hugs its ink down to
	// MIN_TEXT_BOX. The fit is a pure size measurement, so it carries the object's current position:
	// applying it moves nothing, exactly as the size-only write it replaced did.
	protected hugContentMinSize(fallbackWidth: number | null): HugFit
	{
		const def = () => ({ box: { pos: this.box.pos, dims: { width: fallbackWidth ?? 30, height: 20 } }, dy: 0, measured: false });

		if (!this.getContentRef())
			return def();

		const bbox = this.getContentRef().getBBox();
		if (bbox.width === 0 && bbox.height === 0)
			return def();

		const dims = { width: Math.max(MIN_TEXT_BOX, Math.ceil(bbox.width)), height: Math.max(MIN_TEXT_BOX, Math.ceil(bbox.height)) }

		return {
			box: {
				pos:  this.box.pos,
				dims,
			},
			dy: Math.floor(bbox.y),
		}
	}

	refreshSize(clampTop = true): void { this.runFit(clampTop, 0) }

	refreshContent(objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext): void
	{
		const snap: Box = cloneBox(this.box)
		if (!this.fitAfterEdit(snap, v => this.writeGeometry(v), this.contentOffsetY(), this.setContentOffsetY, objCtx, impls, histCtx, this.canvas))
			this.runFit(false, 0)
	}

	// ── Host interaction seam (text overrides) ───────────────────────────────────

	// The initial text fit: run it now, and re-run it once the web-font geometry gate opens (and on every
	// subsequent font load) so the fit that lands in the store is measured against the real font. Called
	// from SelectableObject's setup, so onGeometryReady binds to that reactive owner.
	override setupGeometry(): void
	{
		this.refreshSize()
		onGeometryReady(() => this.refreshSize())
	}

	// Commit a text resize on mouseup. resizeMode is only ever set by the text resize handlers, so this
	// runs the width/corner/field-width end-commits after re-fitting the box.
	override onResizeEnd(mode: TextResizeMode, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext): void
	{
		const box = this.box
		if (mode == 'textCorner' || mode == 'textWidth')
			this.refreshSize(false)
		if (mode == 'textWidth')
			this.onWidthResizeEnd(cloneBox(box), objCtx, impls, histCtx)
		if (mode == 'textCorner')
			this.onCornerResizeEnd(cloneBox(box), objCtx, impls, histCtx)
		if (mode == 'fieldWidth')
			this.onFieldWidthResizeEnd(box.pos.x, objCtx, impls, histCtx)
	}

	override handlesDoubleClick(): boolean { return true }

	// A double-click on an editable text object opens the editor (an image's opens upload/rebind).
	// TextObject renders the edit overlay reactively off the selection's editing state; the intercept
	// rect hides itself while editing via !isEditing(). No-ops until the text element is mounted.
	override handleDoubleClick(e: MouseEvent, selCtx: SelectionContext, _mode: Mode): void
	{
		if (!this.getTextEl()) return
		e.preventDefault()
		e.stopPropagation()
		selCtx.select(this.id)
		selCtx.startEdit(this.id)
	}

	// A design-mode template field draws an "open" selection box sized to its designed (minimum) height,
	// capped at the live box height, anchored to the bounded (aligned) edge. Every other text impl (and
	// every non-text object) keeps the closed dashed rectangle from ObjectImpl's default.
	override selectionBox(h: number): SelectionBox
	{
		if (this.resizeHandles() != 'fieldBox')
			return { open: false, sideTop: 0, sideH: 0, hLineTop: false, hLineBottom: false }
		const sideH = Math.min(h, this.minHeight())
		const va = this.verticalAlign()
		const sideTop =
			va == 'bottom' ? Math.max(0, h - sideH) :
			va == 'center' ? Math.max(0, (h - sideH) / 2) : 0
		return { open: true, sideTop, sideH, hLineTop: va == 'top', hLineBottom: va == 'bottom' }
	}

	// Bracketed by beginFit/endFit (see fitText's fit-settled gate): until this whole chain has landed
	// the element's live geometry is still its unmeasured seed, and the user-mode flatten must not bake
	// it. A retry opens its own fit before this one closes, so the count never reaches zero mid-chain.
	private runFit(clampTop: boolean, attempt: number): void
	{
		const FIT_MAX_RETRIES = 10
//FIXME messy type
		const notReady = (min: { measured?: boolean } | undefined) => min?.measured === false && (this.getText() ?? '') != ''

		beginFit()
		afterRender(() => {
			const hugFit = this.getHugFit()
			if (!hugFit) {
				endFit()
				return;  //XXX or just dont call it
			}

			if (notReady(hugFit) && attempt < FIT_MAX_RETRIES) {
				this.runFit(clampTop, attempt + 1)
				endFit()
				return;
			}

			this.writeGeometry(hugFit.box)

			afterRender(() => {
				const ms2 = this.getHugFit()
				if (!ms2) {
					endFit()
					return
				}
				if (notReady(ms2) && attempt < FIT_MAX_RETRIES) {
					this.runFit(clampTop, attempt + 1)
					endFit()
					return;
				}
				const coy = ms2.dy ?? 0
				this.setContentOffsetY(coy)
				if (clampTop && coy !== 0) this.writePos({ x: this.box.pos.x, y: Math.max(-coy, this.box.pos.y) })
				endFit()
			})
		})
	}
}
